import { useCallback, useEffect, useState } from 'react';
import { useCredits } from '../hooks/useCredits';
import { useAuth } from '../contexts/AuthContext';
import { CreditPurchaseModal } from './credits/CreditPurchaseModal';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CreditTx {
  id: string;
  type: string;
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  description: string | null;
  createdAt: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const TX_TYPE_META: Record<string, { label: string; className: string }> = {
  CONSUME: { label: 'Consumo', className: 'bg-red-50 text-red-600 border-red-200' },
  ADD: { label: 'Recarga', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  BONUS: { label: 'Bônus', className: 'bg-teal-50 text-teal-700 border-teal-200' },
  REFUND: { label: 'Estorno', className: 'bg-sky-50 text-sky-700 border-sky-200' },
  REMOVE: { label: 'Remoção', className: 'bg-red-50 text-red-600 border-red-200' },
  ADJUST: { label: 'Ajuste', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  LATE_CONNECTION_CHARGE: { label: 'Mensalidade conexão', className: 'bg-violet-50 text-violet-700 border-violet-200' },
  PURCHASE: { label: 'Compra Asaas', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  PURCHASE_REVERSAL: { label: 'Estorno Asaas', className: 'bg-red-50 text-red-700 border-red-200' },
};

function txMeta(type: string) {
  return TX_TYPE_META[type] || { label: type, className: 'bg-slate-50 text-slate-600 border-slate-200' };
}

export function formatCredits(value: number) {
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 3 });
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CreditsHistoryView() {
  const { user } = useAuth();
  const { credits, loading: loadingBalance, refetch } = useCredits();
  const [transactions, setTransactions] = useState<CreditTx[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const canPurchase = ['ADMIN', 'TENANT_ADMIN', 'SUPERADMIN'].includes(user?.role || '');

  const loadTransactions = useCallback(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('auth_token');
    const selectedTenantId = localStorage.getItem('superadmin_selected_tenant') || localStorage.getItem('selected_tenant_id');
    const headers: Record<string, string> = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
    if (selectedTenantId) headers['X-Tenant-Id'] = selectedTenantId;
    fetch(`/api/credits/my/transactions?page=${page}&limit=20`, {
      headers,
    })
      .then(res => res.json())
      .then(data => {
        if (!alive) return;
        if (data.success) {
          setTransactions(data.data.transactions || []);
          setPagination(data.data.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 });
        } else {
          setError(data.message || 'Erro ao carregar histórico.');
        }
      })
      .catch(() => { if (alive) setError('Erro ao carregar histórico de créditos.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [page]);

  useEffect(() => loadTransactions(), [loadTransactions]);

  const visible = typeFilter ? transactions.filter(tx => tx.type === typeFilter) : transactions;
  const typesInPage = Array.from(new Set(transactions.map(tx => tx.type)));

  const consumedInPage = transactions
    .filter(tx => tx.amount < 0)
    .reduce((sum, tx) => sum + Math.abs(tx.amount), 0);

  return (
    <div className="space-y-4">
      {/* Cards de resumo */}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-600">Saldo atual</p>
          <p className="mt-1 text-3xl font-black text-slate-900">
            {loadingBalance ? '...' : formatCredits(credits?.balance ?? 0)}
            <span className="ml-1 text-sm font-semibold text-slate-400">créditos</span>
          </p>
          {credits?.updatedAt && (
            <p className="mt-1 text-[11px] text-slate-400">Atualizado em {formatDate(credits.updatedAt)}</p>
          )}
          {canPurchase && (
            <button onClick={() => setPurchaseOpen(true)} className="mt-4 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-emerald-700">
              Comprar mais créditos
            </button>
          )}
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Movimentações registradas</p>
          <p className="mt-1 text-3xl font-black text-slate-900">
            {pagination.total}
          </p>
          <p className="mt-1 text-[11px] text-slate-400">
            Consumido nesta página: <span className="font-semibold text-red-500">-{formatCredits(consumedInPage)}</span>
          </p>
        </div>
      </div>

      {/* Filtro por tipo (na página atual) */}
      {typesInPage.length > 1 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            onClick={() => setTypeFilter('')}
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
              !typeFilter ? 'border-slate-700 bg-slate-800 text-white' : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
            }`}
          >
            Todos
          </button>
          {typesInPage.map(t => (
            <button
              key={t}
              onClick={() => setTypeFilter(typeFilter === t ? '' : t)}
              className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                typeFilter === t ? 'border-slate-700 bg-slate-800 text-white' : `${txMeta(t).className} hover:opacity-80`
              }`}
            >
              {txMeta(t).label}
            </button>
          ))}
        </div>
      )}

      {/* Tabela de transações */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {loading ? (
          <p className="p-8 text-center text-sm text-slate-400 animate-pulse">Carregando histórico...</p>
        ) : error ? (
          <p className="p-8 text-center text-sm text-red-500">{error}</p>
        ) : visible.length === 0 ? (
          <div className="p-10 text-center">
            <p className="font-medium text-slate-400">Nenhuma movimentação de créditos</p>
            <p className="mt-1 text-sm text-slate-500">Recargas e consumos aparecerão aqui automaticamente.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  <th className="px-4 py-3">Data</th>
                  <th className="px-4 py-3">Tipo</th>
                  <th className="px-4 py-3">Descrição</th>
                  <th className="px-4 py-3 text-right">Valor</th>
                  <th className="px-4 py-3 text-right">Saldo após</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(tx => (
                  <tr key={tx.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60">
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-500">{formatDate(tx.createdAt)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-bold ${txMeta(tx.type).className}`}>
                        {txMeta(tx.type).label}
                      </span>
                    </td>
                    <td className="max-w-md px-4 py-3 text-xs text-slate-600">{tx.description || '—'}</td>
                    <td className={`whitespace-nowrap px-4 py-3 text-right font-mono text-xs font-bold ${tx.amount < 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                      {tx.amount > 0 ? '+' : ''}{formatCredits(tx.amount)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-xs text-slate-500">{formatCredits(tx.balanceAfter)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Paginação */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-400">
            Página {pagination.page} de {pagination.totalPages} · {pagination.total} movimentações
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
            >
              ← Anterior
            </button>
            <button
              onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
              disabled={page >= pagination.totalPages || loading}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
            >
              Próxima →
            </button>
          </div>
        </div>
      )}
      <CreditPurchaseModal
        open={purchaseOpen}
        defaultName={user?.nome}
        defaultEmail={user?.email}
        onClose={() => setPurchaseOpen(false)}
        onPaid={async () => {
          await refetch();
          setPage(1);
          loadTransactions();
        }}
      />
    </div>
  );
}
